import React from 'react';
import {Input, Spin} from 'antd';
import {ApartmentOutlined, FolderOutlined, ProjectOutlined} from '@ant-design/icons';
import {useWorkspaceSearch} from '@/hooks/workspace';
import type {WorkspaceSearchHit, WorkspaceSearchType} from '@/types/api';
import './WorkspaceSearch.css';

type Props = {
	onSelect: (hit: WorkspaceSearchHit) => void;
};

const ICONS: Record<WorkspaceSearchType, React.ReactNode> = {
	PROJECT: <ProjectOutlined />,
	FOLDER: <FolderOutlined />,
	DIAGRAM: <ApartmentOutlined />,
};

const WorkspaceSearch: React.FC<Props> = ({onSelect}) => {
	const {query, setQuery, hits, hasMore, loading, loadingMore, loadMore, open, setOpen, clear} =
		useWorkspaceSearch();
	const [activeIndex, setActiveIndex] = React.useState(0);
	const rootRef = React.useRef<HTMLDivElement>(null);
	const sentinelRef = React.useRef<HTMLLIElement>(null);

	React.useEffect(() => {
		setActiveIndex(0);
	}, [query]);

	React.useEffect(() => {
		const onPointerDown = (event: MouseEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
		};
		document.addEventListener('mousedown', onPointerDown);
		return () => document.removeEventListener('mousedown', onPointerDown);
	}, [setOpen]);

	const pick = (hit: WorkspaceSearchHit) => {
		clear();
		onSelect(hit);
	};

	const showPanel = open && query.trim().length > 0;

	React.useEffect(() => {
		const node = sentinelRef.current;
		const root = node?.parentElement;
		if (!node || !root) return;
		const observer = new IntersectionObserver(
			entries => {
				if (entries.some(entry => entry.isIntersecting)) loadMore();
			},
			{root, rootMargin: '24px'}
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [loadMore, showPanel, hasMore, hits.length]);

	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Escape') {
			setOpen(false);
			return;
		}
		if (!showPanel || hits.length === 0) return;
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setActiveIndex(index => Math.min(hits.length - 1, index + 1));
		}
		if (event.key === 'ArrowUp') {
			event.preventDefault();
			setActiveIndex(index => Math.max(0, index - 1));
		}
		if (event.key === 'Enter') {
			event.preventDefault();
			const hit = hits[activeIndex];
			if (hit) pick(hit);
		}
	};

	return (
		<div className="workspace-search" ref={rootRef}>
			<Input
				allowClear
				value={query}
				placeholder="Поиск по имени"
				aria-label="Поиск по проектам, папкам и диаграммам"
				aria-expanded={showPanel}
				aria-controls="workspace-search-results"
				onChange={event => {
					setQuery(event.target.value);
					setOpen(true);
				}}
				onFocus={() => setOpen(true)}
				onKeyDown={onKeyDown}
			/>
			{showPanel ? (
				<ul id="workspace-search-results" className="workspace-search-panel" role="listbox">
					{loading && hits.length === 0 ? (
						<li className="workspace-search-status">
							<Spin size="small" />
						</li>
					) : hits.length === 0 ? (
						<li className="workspace-search-status">Ничего не найдено</li>
					) : (
						hits.map((hit, index) => (
							<li key={`${hit.type}:${hit.id}`}>
								<button
									type="button"
									role="option"
									aria-selected={index === activeIndex}
									className={
										index === activeIndex
											? 'workspace-search-hit is-active'
											: 'workspace-search-hit'
									}
									onMouseEnter={() => setActiveIndex(index)}
									onMouseDown={event => event.preventDefault()}
									onClick={() => pick(hit)}
								>
									<span className="workspace-search-hit-icon">{ICONS[hit.type]}</span>
									<span className="workspace-search-hit-body">
										<span className="workspace-search-hit-name">{hit.name}</span>
										{hit.path ? (
											<span className="workspace-search-hit-path" title={hit.path}>
												{hit.path}
											</span>
										) : null}
									</span>
								</button>
							</li>
						))
					)}
					{hasMore ? (
						<li ref={sentinelRef} className="workspace-search-status">
							{loadingMore ? <Spin size="small" /> : null}
						</li>
					) : null}
				</ul>
			) : null}
		</div>
	);
};

export default WorkspaceSearch;
